import React, { FC, useCallback, useMemo } from 'react';
import Masthead, { MastheadLink, MastheadTitle } from '@amzn/meridian/masthead';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import { INTAKE_FOLDER_PATH, TICKETING_PATH } from 'src/constants/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '@amzn/meridian/button';
import { FEEDBACK_TEXT, REPORT_ISSUE } from '@constants/strings';
import FeatureName from '@constants/featureName';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import useFeatureFlagControls from 'src/hooks/useFeatureFlagControls';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { NavPath, ProgramFeatures } from 'src/types/programConfig';

/**
 * Maps navPath URLs to their corresponding feature toggle key.
 * Paths not listed here are always shown (e.g., productGroups).
 */
const navPathFeatureToggleMap: Partial<Record<string, keyof ProgramFeatures>> = {
  zoneCreation: 'zoneMap',
  agileManagement: 'itemAssortment',
  assortmentSearch: 'assortmentSearch',
  productReadiness: 'productReadiness',
};

const AgileNavBar: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const nextGenEnabled = useFeatureFlag(FeatureName.NEXT_GEN_V2);
  const { setFeatureStatus } = useFeatureFlagControls();
  const config = useProgramConfig();

  // Filter navPaths based on feature toggles
  const visibleNavPaths: NavPath[] = useMemo(() => {
    return config.navPaths.filter((navPath) => {
      const featureKey = navPathFeatureToggleMap[navPath.url];
      // If no feature toggle is mapped, always show the link
      if (!featureKey) return true;
      return config.features[featureKey];
    });
  }, [config]);

  // Check if demo mode is enabled via URL parameter
  const searchParams = new URLSearchParams(location.search);
  const isDemoMode = searchParams.get('demo') === '1';
  // Preserve current query params (storeId, date, etc.) when navigating between pages
  const handleNavClick = useCallback(
    (href: string) => {
      navigate(`${href}${location.search}`);
    },
    [navigate, location.search],
  );

  const handleToggleChange = (checked: boolean) => {
    setFeatureStatus(FeatureName.NEXT_GEN_V2, checked);
  };
  return (
    <Masthead size="large" data-cy="agile_nav_bar">
      <MastheadTitle href={config.routePrefix}>
        <Text type="h400">{config.programType}</Text>
      </MastheadTitle>
      {visibleNavPaths.map((path) => (
        <MastheadLink
          href={path.url}
          onClick={handleNavClick}
          selected={location.pathname.replace(/^\/[^/]+\//, '') === path.url}
          key={`link_${path.url}`}
        >
          {path.name}
        </MastheadLink>
      ))}
      {isDemoMode && (
        <Toggle checked={nextGenEnabled} onChange={handleToggleChange} size="small" data-cy="next_gen_v2_toggle">
          <Text type="b300">V2</Text>
        </Toggle>
      )}
      <Button href={TICKETING_PATH} type="tertiary" rel="noreferrer" target="_blank" data-cy="report_issue_button">
        {REPORT_ISSUE}
      </Button>
      <Button href={INTAKE_FOLDER_PATH} type="tertiary" rel="noreferrer" target="_blank" data-cy="feedback_button">
        {FEEDBACK_TEXT}
      </Button>
    </Masthead>
  );
};
export default AgileNavBar;
